import { downloadZip } from 'client-zip';
import { buildAlbumHtml, type HtmlItem } from './exportHtml';
import { partFolderName, sanitizeAlbumName, thumbName } from './exportNaming';
import { ExportFatalError, type ExportItem, type ExportSink, type FetchFn } from './types';

interface ZipEntry { name: string; input: Blob | string }

/** An async iterable that yields values as they are pushed, until closed or failed. */
function createQueue<T>() {
  const buffer: T[] = [];
  let wake: (() => void) | null = null;
  let state: 'open' | 'closed' | 'failed' = 'open';
  return {
    push(value: T) { buffer.push(value); wake?.(); },
    close() { if (state === 'open') state = 'closed'; wake?.(); },
    /** Abandon the archive: drain() throws, so nothing more is assembled. */
    fail() { state = 'failed'; wake?.(); },
    async *drain(): AsyncGenerator<T> {
      for (;;) {
        if (state === 'failed') throw new Error('ZIP part discarded');
        if (buffer.length > 0) { yield buffer.shift() as T; continue; }
        if (state === 'closed') return;
        await new Promise<void>(resolve => { wake = resolve; });
        wake = null;
      }
    },
  };
}

export interface ZipSinkOptions {
  title: string;
  /** Appended to the ZIP and folder name, e.g. " (new 2026-10-13)" for an only-new export. */
  nameSuffix?: string;
  /** Items grouped into parts, from planParts. */
  parts: ExportItem[][];
  /** Number of the first part. Greater than 1 when retrying after an earlier export. */
  startPartNumber?: number;
  save: (blob: Blob, filename: string) => void;
  fetchFn?: FetchFn;
}

/** Sink that produces one ZIP per part, each with its own index.html. Does not resume. */
export function createZipSink(options: ZipSinkOptions): ExportSink {
  const { title, nameSuffix = '', parts, startPartNumber = 1, save, fetchFn = fetch } = options;
  const base = `${sanitizeAlbumName(title)}${nameSuffix}`;
  const numbered = parts.length > 1 || startPartNumber > 1;
  let partError: string | null = null;

  const states = parts.map((items, index) => {
    const queue = createQueue<ZipEntry>();
    const zip: Promise<Blob> = downloadZip(queue.drain()).blob();
    zip.catch(() => undefined); // observed later in closePart; never an unhandled rejection
    return {
      items,
      number: startPartNumber + index,
      folder: partFolderName(base, startPartNumber + index, numbered),
      queue,
      zip: zip as Promise<Blob> | null,
      added: new Map<string, HtmlItem>(),
      pending: items.length,
      closed: false,
    };
  });
  type PartState = (typeof states)[number];
  const partOf = new Map<string, PartState>();
  states.forEach(state => state.items.forEach(item => partOf.set(item.id, state)));

  const closePart = async (part: PartState, keep: boolean) => {
    if (part.closed) return;
    part.closed = true;
    const zip = part.zip;
    part.zip = null; // drop the sink's reference; the blob is freed once saved
    const present = part.items.flatMap(item => part.added.get(item.id) ?? []);
    if (!keep || present.length === 0 || !zip) {
      part.queue.fail(); // nothing to save: do not assemble the archive
      await zip?.catch(() => undefined);
      return;
    }
    const partLabel = numbered ? `Part ${part.number}` : null;
    part.queue.push({
      name: `${part.folder}/index.html`,
      input: buildAlbumHtml({ title, items: present, partLabel, exportedAt: new Date() }),
    });
    part.queue.close();
    save(await zip, `${part.folder}.zip`);
  };

  return {
    async has() {
      return false;
    },

    async write(item, signal) {
      const part = partOf.get(item.id);
      if (!part) throw new Error(`No ZIP part planned for ${item.filename}`);
      try {
        const res = await fetchFn(item.url, { signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const original = await res.blob();
        const thumb = item.thumbnailUrl
          ? await fetchFn(item.thumbnailUrl, { signal }).then(r => (r.ok ? r.blob() : null)).catch(() => null)
          : null;
        part.queue.push({ name: `${part.folder}/media/${item.filename}`, input: original });
        if (thumb) part.queue.push({ name: `${part.folder}/thumbs/${thumbName(item.filename)}`, input: thumb });
        part.added.set(item.id, { filename: item.filename, mediaType: item.mediaType, hasThumb: thumb !== null });
      } finally {
        part.pending -= 1;
        if (part.pending === 0 && !signal.aborted) {
          await closePart(part, true).catch(err => {
            partError ??= err instanceof Error ? err.message : 'unknown error';
          });
        }
      }
    },

    async finish() {
      // Any part still open here was interrupted (cancelled or fatal): discard it.
      await Promise.all(states.map(state => closePart(state, false)));
      if (partError !== null) {
        throw new ExportFatalError(`A ZIP file could not be created (${partError}). Try again, or save to a folder instead.`);
      }
    },
  };
}
